/**
 * Login por USUÁRIO + CÓDIGO DA EMPRESA + SENHA (cadastro direto, sem e-mail).
 *
 * A conta continua no Supabase Auth: o endereço da conta é INTERNO e calculado aqui, igual no
 * ERP, no aplicativo e no banco (`app.login_email`, migration 0024):
 *
 *   <usuário>@<código>.usuarios.cafegestor.invalid
 *
 * O domínio `.invalid` é reservado (RFC 2606): nunca recebe mensagens. Usuário e código não têm
 * "@" e o código não tem ponto, então cada par (usuário, código) dá um endereço único — o mesmo
 * usuário em empresas diferentes é sempre outra conta.
 */
import { z } from "zod";

export const LOGIN_EMAIL_DOMAIN = "usuarios.cafegestor.invalid";
/** Senha mínima (a mesma da tela "Defina sua senha"); 72 é o limite do bcrypt do Supabase. */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 72;

const LOGIN_CODE_RE = /^[a-z0-9]{3,20}$/;
const USERNAME_RE = /^[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/;

export const loginCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(LOGIN_CODE_RE, "Código: 3 a 20 letras minúsculas ou números, sem espaços (ex.: grao21)");

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    USERNAME_RE,
    "Usuário: 3 a 30 caracteres — letras minúsculas, números, ponto, hífen ou sublinhado",
  );

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres`)
  .max(PASSWORD_MAX_LENGTH, `A senha pode ter no máximo ${PASSWORD_MAX_LENGTH} caracteres`);

/** Endereço interno da conta (sem validar: use os schemas antes). */
export function loginEmailFor(username: string, loginCode: string): string {
  return `${username.trim().toLowerCase()}@${loginCode.trim().toLowerCase()}.${LOGIN_EMAIL_DOMAIN}`;
}

/** Usuário e código de um endereço interno; null para e-mails comuns. */
export function parseLoginEmail(email: string): { username: string; loginCode: string } | null {
  const m = /^([^@]+)@([a-z0-9]+)\.usuarios\.cafegestor\.invalid$/.exec(email.trim().toLowerCase());
  if (!m || !USERNAME_RE.test(m[1]!) || !LOGIN_CODE_RE.test(m[2]!)) return null;
  return { username: m[1]!, loginCode: m[2]! };
}

/** Endereço interno nunca é mostrado: a tela usa o usuário. */
export const isLoginEmail = (email: string) => parseLoginEmail(email) !== null;

/** Tela de login: usuário + código + senha (o código fica lembrado no aparelho). */
export const usernameSignInSchema = z.object({
  username: usernameSchema,
  loginCode: loginCodeSchema,
  password: z.string().min(1, "Informe a senha"),
});
export type UsernameSignInInput = z.infer<typeof usernameSignInSchema>;

// ---------------------------------------------------------------- API
export const setLoginCodeSchema = z.object({ loginCode: loginCodeSchema });
export type SetLoginCodeInput = z.infer<typeof setLoginCodeSchema>;

const optionalContactEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === "" || z.string().email().safeParse(v).success, "E-mail inválido")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

export const createLoginUserSchema = z.object({
  fullName: z.string().trim().min(2, "Informe o nome completo").max(120),
  username: usernameSchema,
  password: passwordSchema,
  roleId: z.string().uuid("Selecione o papel"),
  active: z.boolean(),
  contactEmail: optionalContactEmail,
});
export type CreateLoginUserInput = z.infer<typeof createLoginUserSchema>;

export type CreatedLoginUser = {
  membershipId: string;
  userId: string;
  username: string;
  loginCode: string;
  fullName: string;
  status: "active" | "suspended";
  /** a conta no Supabase ficou bloqueada (inativa) / liberada como pedido */
  loginBlocked: boolean;
};

export const resetLoginPasswordSchema = z.object({ password: passwordSchema });
export type ResetLoginPasswordInput = z.infer<typeof resetLoginPasswordSchema>;

/**
 * Troca da própria senha pela API. Na troca obrigatória (senha inicial ou redefinida) a senha
 * atual não é pedida: a pessoa acabou de entrar com ela. Fora dela, a senha atual é exigida.
 */
export const changeOwnPasswordSchema = z.object({
  newPassword: passwordSchema,
  currentPassword: z.string().max(PASSWORD_MAX_LENGTH).optional(),
});
export type ChangeOwnPasswordInput = z.infer<typeof changeOwnPasswordSchema>;

/** Códigos de erro do login que as telas tratam. */
export const LOGIN_ERROR = {
  /** senha inicial/redefinida: só "trocar senha" e "meus dados" funcionam até a troca */
  passwordChangeRequired: "TROCA_DE_SENHA_OBRIGATORIA",
  /** a senha mudou depois deste login: entre de novo */
  sessionEnded: "SESSAO_ENCERRADA",
} as const;

// ---------------------------------------------------------------- tela "Usuário + Senha"
/**
 * Empresa da INSTALAÇÃO (ERP publicado ou APK do piloto), definida na geração — o funcionário
 * não digita. Inválida ou ausente = null (a tela volta a pedir o código da empresa).
 */
export function loginCodeOfInstallation(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const parsed = loginCodeSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export type LoginAccount =
  { ok: true; email: string; mode: "usuario" | "email" } | { ok: false; message: string };

/**
 * Conta do Supabase para o que foi digitado no campo "Usuário" de uma instalação com empresa:
 *  - sem "@": usuário DESTA empresa (endereço interno com o código da instalação);
 *  - com "@": login por e-mail (contas de convite, como a da administradora);
 *  - endereço interno digitado (…@codigo.usuarios.cafegestor.invalid) é recusado: o funcionário
 *    entra pelo usuário, e uma instalação nunca é porta para conta de outra empresa.
 * A senha não passa por aqui. A proteção de verdade continua sendo senha + vínculo + RLS.
 */
export function loginAccountFor(input: string, installationCode: string): LoginAccount {
  const value = input.trim();
  if (!value) return { ok: false, message: "Informe o usuário." };
  if (value.includes("@")) {
    if (isLoginEmail(value) || value.toLowerCase().endsWith(`.${LOGIN_EMAIL_DOMAIN}`)) {
      return { ok: false, message: "Entre com o seu usuário (sem @) ou com o seu e-mail." };
    }
    const email = z.string().trim().toLowerCase().email().safeParse(value);
    if (!email.success) return { ok: false, message: "E-mail inválido." };
    return { ok: true, email: email.data, mode: "email" };
  }
  const username = usernameSchema.safeParse(value);
  if (!username.success) {
    return { ok: false, message: username.error.issues[0]?.message ?? "Usuário inválido." };
  }
  return { ok: true, email: loginEmailFor(username.data, installationCode), mode: "usuario" };
}
